import React, { useState } from 'react';
import { Button, DatePicker, Input, Spin, Table, Divider, Radio } from 'antd';
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { RadioChangeEvent } from 'antd';
import dayjs from 'dayjs';
import { ReportFilter, DetailItem, SubtotalItem, CurrencyTotal, UnitTotal } from '../../services/c330Api';
import { useC330Report } from './hooks/useC330';
import './C330Form.css';

const { RangePicker } = DatePicker;

const SORT_OPTIONS = [
  { label: '單號別', value: 1 },
  { label: '日期別', value: 2 },
  { label: '客戶別', value: 3 },
];

const C330Form: React.FC = () => {
  const { loading, reportData, queried, noData, runQuery } = useC330Report();

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [docNoStart, setDocNoStart] = useState('');
  const [docNoEnd, setDocNoEnd] = useState('');
  const [customerStart, setCustomerStart] = useState('');
  const [customerEnd, setCustomerEnd] = useState('');
  const [productStart, setProductStart] = useState('');
  const [productEnd, setProductEnd] = useState('');
  const [warehouseStart, setWarehouseStart] = useState('');
  const [warehouseEnd, setWarehouseEnd] = useState('');
  const [sortMode, setSortMode] = useState(1);

  const handleQuery = () => {
    const filter: ReportFilter = {
      date_start: dateRange?.[0]?.format('YYYY-MM-DD'),
      date_end: dateRange?.[1]?.format('YYYY-MM-DD'),
      doc_no_start: docNoStart || undefined,
      doc_no_end: docNoEnd || undefined,
      customer_start: customerStart || undefined,
      customer_end: customerEnd || undefined,
      product_start: productStart || undefined,
      product_end: productEnd || undefined,
      warehouse_start: warehouseStart || undefined,
      warehouse_end: warehouseEnd || undefined,
      sort_mode: sortMode,
    };
    runQuery(filter);
  };

  const handlePrint = () => {
    if (!reportData) return;
    const sortLabel = SORT_OPTIONS.find(o => o.value === reportData.sort_mode)?.label || '';
    const now = new Date().toLocaleString('zh-TW');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>銷售退回明細表</title>
<style>
  body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}
  h2{text-align:center;margin-bottom:4px}
  .meta{text-align:center;font-size:11px;color:#555;margin-bottom:12px}
  table{width:100%;border-collapse:collapse;margin-bottom:16px}
  th,td{border:1px solid #333;padding:3px 6px}
  th{background:#f0f0f0;text-align:center}
  .right{text-align:right}.center{text-align:center}
  .section-title{font-weight:bold;background:#e8e8e8}
  @media print{body{margin:10mm}}
</style></head><body>
<h2>銷售退回明細表 (${sortLabel})</h2>
<div class="meta">列印時間: ${now}</div>
<table><thead>
<tr class="section-title"><th colspan="10">明細資料</th></tr>
<tr><th>退回單號</th><th>日期</th><th>客戶</th><th>產品代號</th><th>品名</th>
<th>單位</th><th class="right">數量</th><th class="right">單價</th>
<th class="right">金額</th><th class="right">折算金額</th></tr>
</thead><tbody>
${reportData.details.map(r=>`<tr>
<td class="center">${r.doc_no}</td><td class="center">${r.return_date}</td>
<td class="center">${r.customer_code}</td><td class="center">${r.product_code}</td>
<td>${r.product_name}</td><td class="center">${r.unit}</td>
<td class="right">${r.qty.toFixed(2)}</td><td class="right">${r.unit_price.toFixed(3)}</td>
<td class="right">${r.amount.toFixed(3)}</td><td class="right">${r.rmb_amount.toFixed(2)}</td>
</tr>`).join('')}
</tbody></table>
<table><thead>
<tr class="section-title"><th colspan="5">分組小計</th></tr>
<tr><th>分組</th><th>幣別</th><th>單位</th><th class="right">數量</th><th class="right">金額</th></tr>
</thead><tbody>
${reportData.subtotals.map(r=>`<tr>
<td class="center">${r.group_key}</td><td class="center">${r.currency}</td>
<td class="center">${r.unit}</td><td class="right">${r.total_qty.toFixed(2)}</td>
<td class="right">${r.total_amt.toFixed(3)}</td>
</tr>`).join('')}
</tbody></table>
<table><thead>
<tr class="section-title"><th colspan="2">幣別合計</th></tr>
<tr><th>幣別</th><th class="right">金額</th></tr>
</thead><tbody>
${reportData.currency_totals.map(r=>`<tr>
<td class="center">${r.currency}</td><td class="right">${r.total_amt.toFixed(3)}</td>
</tr>`).join('')}
</tbody></table>
<p><strong>合計折算金額: ${reportData.grand_total.total_rmb.toFixed(2)}</strong></p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const groupLabel = sortMode === 1 ? '退回單號' : sortMode === 2 ? '退回日期' : '客戶代號';

  const detailColumns: ColumnsType<DetailItem> = [
    { title: '退回單號', dataIndex: 'doc_no', key: 'doc_no', align: 'center', width: 90 },
    { title: '退回日期', dataIndex: 'return_date', key: 'return_date', align: 'center', width: 100 },
    { title: '客戶代號', dataIndex: 'customer_code', key: 'customer_code', align: 'center', width: 90 },
    { title: '產品代號', dataIndex: 'product_code', key: 'product_code', align: 'center', width: 100 },
    { title: '品名', dataIndex: 'product_name', key: 'product_name', width: 120 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', align: 'center', width: 50 },
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 50 },
    { title: '數量', dataIndex: 'qty', key: 'qty', align: 'right', width: 80, render: (v: number) => v.toFixed(2) },
    { title: '單價', dataIndex: 'unit_price', key: 'unit_price', align: 'right', width: 80, render: (v: number) => v.toFixed(3) },
    { title: '金額', dataIndex: 'amount', key: 'amount', align: 'right', width: 90, render: (v: number) => v.toFixed(3) },
    { title: '折算金額', dataIndex: 'rmb_amount', key: 'rmb_amount', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
  ];

  const subtotalColumns: ColumnsType<SubtotalItem> = [
    { title: groupLabel, dataIndex: 'group_key', key: 'group_key', align: 'center', width: 100 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', align: 'center', width: 60 },
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 60 },
    { title: '總數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right', width: 90, render: (v: number) => v.toFixed(2) },
    { title: '總金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 100, render: (v: number) => v.toFixed(3) },
    { title: '折算金額', dataIndex: 'total_rmb', key: 'total_rmb', align: 'right', width: 100, render: (v: number) => v.toFixed(2) },
  ];

  const currencyColumns: ColumnsType<CurrencyTotal> = [
    { title: '幣別', dataIndex: 'currency', key: 'currency', align: 'center', width: 80 },
    { title: '總金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 130, render: (v: number) => v.toFixed(3) },
  ];

  const unitColumns: ColumnsType<UnitTotal> = [
    { title: '單位', dataIndex: 'unit', key: 'unit', align: 'center', width: 80 },
    { title: '總數量', dataIndex: 'total_qty', key: 'total_qty', align: 'right', width: 120, render: (v: number) => v.toFixed(2) },
    { title: '折算金額', dataIndex: 'total_amt', key: 'total_amt', align: 'right', width: 130, render: (v: number) => v.toFixed(2) },
  ];

  return (
    <Spin spinning={loading}>
      <div className="c330-container">
        <h2 className="c330-title">銷售退回明細表 (pwc330f1)</h2>

        <div className="c330-filter-panel">
          <div className="c330-filter-row">
            <span className="c330-filter-label">退回日期</span>
            <RangePicker
              value={dateRange}
              onChange={(v) => setDateRange(v as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
              format="YYYY-MM-DD"
              placeholder={['起始日期', '結束日期']}
              style={{ width: 280 }}
            />
          </div>
          <div className="c330-filter-row">
            <span className="c330-filter-label">退回單號</span>
            <Input value={docNoStart} onChange={e => setDocNoStart(e.target.value)} placeholder="起" style={{ width: 120 }} maxLength={8} />
            <span className="c330-filter-sep">~</span>
            <Input value={docNoEnd} onChange={e => setDocNoEnd(e.target.value)} placeholder="迄" style={{ width: 120 }} maxLength={8} />
          </div>
          <div className="c330-filter-row">
            <span className="c330-filter-label">客戶代號</span>
            <Input value={customerStart} onChange={e => setCustomerStart(e.target.value)} placeholder="起" style={{ width: 120 }} maxLength={12} />
            <span className="c330-filter-sep">~</span>
            <Input value={customerEnd} onChange={e => setCustomerEnd(e.target.value)} placeholder="迄" style={{ width: 120 }} maxLength={12} />
          </div>
          <div className="c330-filter-row">
            <span className="c330-filter-label">產品代號</span>
            <Input value={productStart} onChange={e => setProductStart(e.target.value)} placeholder="起 (色號1+色號2)" style={{ width: 160 }} maxLength={12} />
            <span className="c330-filter-sep">~</span>
            <Input value={productEnd} onChange={e => setProductEnd(e.target.value)} placeholder="迄 (色號1+色號2)" style={{ width: 160 }} maxLength={12} />
          </div>
          <div className="c330-filter-row">
            <span className="c330-filter-label">庫位</span>
            <Input value={warehouseStart} onChange={e => setWarehouseStart(e.target.value)} placeholder="起" style={{ width: 100 }} maxLength={5} />
            <span className="c330-filter-sep">~</span>
            <Input value={warehouseEnd} onChange={e => setWarehouseEnd(e.target.value)} placeholder="迄" style={{ width: 100 }} maxLength={5} />
          </div>
          <div className="c330-filter-row">
            <span className="c330-filter-label">排序方式</span>
            <Radio.Group
              value={sortMode}
              onChange={(e: RadioChangeEvent) => setSortMode(e.target.value)}
              options={SORT_OPTIONS}
              optionType="button"
              buttonStyle="solid"
              size="small"
            />
          </div>
          <div className="c330-filter-actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查詢</Button>
            {reportData && (
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印預覽</Button>
            )}
          </div>
        </div>

        {queried && !noData && reportData && (
          <div>
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>明細資料</Divider>
            <Table<DetailItem>
              dataSource={reportData.details}
              columns={detailColumns}
              rowKey={(r) => `${r.doc_no}-${r.detail_seq}`}
              size="small" pagination={false} scroll={{ x: 1000 }} bordered className="c330-table"
            />

            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
              分組小計（{groupLabel}+幣別+單位）
            </Divider>
            <Table<SubtotalItem>
              dataSource={reportData.subtotals}
              columns={subtotalColumns}
              rowKey={(r) => `${r.group_key}-${r.currency}-${r.unit}`}
              size="small" pagination={false} scroll={{ x: 550 }} bordered className="c330-table"
            />

            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>幣別合計</Divider>
            <Table<CurrencyTotal>
              dataSource={reportData.currency_totals}
              columns={currencyColumns}
              rowKey={(r) => r.currency}
              size="small" pagination={false} scroll={{ x: 250 }} bordered className="c330-table"
            />

            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>單位合計</Divider>
            <Table<UnitTotal>
              dataSource={reportData.unit_totals}
              columns={unitColumns}
              rowKey={(r) => r.unit}
              size="small" pagination={false} scroll={{ x: 350 }} bordered
            />

            <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 'bold', padding: '12px 0' }}>
              合計折算金額: {reportData.grand_total.total_rmb.toFixed(2)}
            </div>
          </div>
        )}

        {queried && noData && (
          <div className="c330-no-data">查無符合條件的銷貨退回資料!</div>
        )}
      </div>
    </Spin>
  );
};

export default C330Form;
